import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Link } from '@tanstack/react-router';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import {
  Building,
  Check,
  Globe,
  LogOut,
  Monitor,
  Moon,
  Search,
  Settings,
  Sun,
  User,
} from 'lucide-react';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/new-ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/new-ui/dropdown-menu';
import { useTheme } from '@/components/providers/theme';
import { useUpdateLanguage } from '@/features/account/api/update-language';
import { useLogOut } from '@/features/auth/api/logout';
import { useDebouncedCallback } from '@/hooks/use-debounced-callback';
import { readRecentTenantIds } from '@/lib/recent-tenants';
import { cn } from '@/lib/utils';
import { getAvatarFallback } from '@/utils/avatar';

import { useShellSidebar } from './shell-sidebar-context';
import { TenantSwitcherDialog, useTenantSwitcher } from './tenant-switcher-dialog';

const rowClass = 'flex h-9 cursor-pointer items-center gap-3 rounded-md px-2.5 text-sm';
const iconClass = 'h-[18px] w-[18px] shrink-0 text-muted-foreground';

const MAX_RECENT_TENANTS = 5;

/** Avatar menu in the header: profile, tenant, setup, theme, language and logout. */
export const UserMenu = () => {
  const [tenantDialogOpen, setTenantDialogOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const { isMobile } = useShellSidebar();
  const { theme, setTheme } = useTheme();
  const isThemeSwitcherEnabled = useFeatureFlagEnabled('theme-switcher') === true;
  const { logout } = useLogOut();
  const { tenants, accessibleTenants, selectedTenant, me, showAllTenantsOption, switchTenant } =
    useTenantSwitcher();

  const updateLanguage = useUpdateLanguage();
  const updateLanguageDebounced = useDebouncedCallback(updateLanguage.mutate);
  const currentLang = i18n.language;

  const changeLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
    updateLanguageDebounced({ language: lang });
  };

  const handleLogout = async () => {
    await logout();
    // this seems to be the most reliable way to start the user journey from scratch
    window.location.reload();
  };

  const user = me.data;
  const displayName = [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email;
  const mediaId = user.images?.mediaId;
  const initials = getAvatarFallback(user);
  const hasTenantAccess = accessibleTenants.length > 0;
  const setupTenantId = selectedTenant?.id;

  const themeOptions = [
    ['light', t('shellThemeLight'), Sun],
    ['dark', t('shellThemeDark'), Moon],
    ['system', t('shellThemeSystem'), Monitor],
  ] as const;
  const activeTheme = themeOptions.find(([value]) => value === theme) ?? themeOptions[0];
  const ThemeIcon = activeTheme[2];

  // A sub-tenant is shown together with its parent, like the branches in the design
  const tenantLabels = new Map(
    tenants.flatMap((tenant) => [
      [tenant.id, tenant.name] as const,
      ...(tenant.subTenants ?? []).map(
        (subTenant) => [subTenant.id, `${tenant.name} · ${subTenant.name}`] as const
      ),
    ])
  );

  // Last five tenant choices; falls back to the first tenants and sub-tenants before anything is picked
  const knownRecentIds = readRecentTenantIds().filter((id) => tenantLabels.has(id));
  const recentTenants = (
    knownRecentIds.length
      ? knownRecentIds
          .map((id) => accessibleTenants.find((tenant) => tenant.id === id))
          .filter((tenant) => tenant !== undefined)
      : accessibleTenants
  ).slice(0, MAX_RECENT_TENANTS);

  const tenantRowContent = (
    <>
      <Building className={iconClass} />
      <span className="flex min-w-0 flex-col">
        <span className="truncate">{t('tenant')}</span>
        <span className="truncate text-xs text-muted-foreground">
          {selectedTenant?.name ?? (hasTenantAccess ? t('allTenants') : t('no-access'))}
        </span>
      </span>
    </>
  );

  return (
    <>
      <DropdownMenu>
        {/* Compact avatar trigger in the header */}
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={displayName}
            title={displayName}
            className="relative rounded-full outline-none ring-offset-background transition-shadow hover:ring-2 hover:ring-border focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 data-[state=open]:ring-2 data-[state=open]:ring-border"
          >
            <Avatar className="h-9 w-9">
              {mediaId ? (
                <AvatarImage src={`/images/${mediaId}?size=thumbnail100`} alt="avatar" />
              ) : null}
              <AvatarFallback className="bg-primary/10 text-xs font-semibold text-primary dark:bg-primary/20">
                {initials}
              </AvatarFallback>
            </Avatar>
          </button>
        </DropdownMenuTrigger>

        {/* Compact, single-line menu that opens below the avatar */}
        <DropdownMenuContent
          side="bottom"
          align="end"
          sideOffset={8}
          className="w-64 rounded-xl p-1.5 shadow-lg"
        >
          <div className="truncate px-2.5 pb-1 pt-1.5 text-[13px] text-muted-foreground">
            {user.email}
          </div>

          <DropdownMenuItem asChild className={rowClass}>
            <Link to="/profile">
              <User className={iconClass} />
              <span className="truncate">{t('profile')}</span>
            </Link>
          </DropdownMenuItem>

          {/* Phones: a side submenu doesn't fit, so the tenant row opens the searchable dialog */}
          {isMobile ? (
            <DropdownMenuItem
              onSelect={() => setTenantDialogOpen(true)}
              className={cn(rowClass, 'h-auto py-2')}
            >
              {tenantRowContent}
            </DropdownMenuItem>
          ) : (
            // Wider screens: submenu with the last five choices, then "Search tenants" opens the searchable dialog
            <DropdownMenuSub>
              <DropdownMenuSubTrigger
                className={cn(rowClass, 'h-auto py-2 [&>svg:last-child]:text-muted-foreground')}
              >
                {tenantRowContent}
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="w-64 rounded-xl p-1.5">
                {showAllTenantsOption && (
                  <DropdownMenuItem onSelect={() => switchTenant()} className={rowClass}>
                    <span className="min-w-0 flex-1 truncate">{t('allTenants')}</span>
                    {!selectedTenant && <Check className="ml-auto h-4 w-4 shrink-0 text-primary" />}
                  </DropdownMenuItem>
                )}
                {recentTenants.map((tenant) => (
                  <DropdownMenuItem
                    key={tenant.id}
                    onSelect={() => switchTenant(tenant)}
                    className={rowClass}
                  >
                    <span
                      className="min-w-0 flex-1 truncate"
                      title={tenantLabels.get(tenant.id) ?? tenant.name}
                    >
                      {tenantLabels.get(tenant.id) ?? tenant.name}
                    </span>
                    {tenant.id === selectedTenant?.id && (
                      <Check className="ml-auto h-4 w-4 shrink-0 text-primary" />
                    )}
                  </DropdownMenuItem>
                ))}
                {(showAllTenantsOption || recentTenants.length > 0) && (
                  <DropdownMenuSeparator className="mx-2.5 my-1.5" />
                )}
                <DropdownMenuItem onSelect={() => setTenantDialogOpen(true)} className={rowClass}>
                  <Search className={iconClass} />
                  <span className="truncate">{t('shellSearchTenantsMenu')}</span>
                </DropdownMenuItem>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          )}

          {user.canAccessSetup && setupTenantId ? (
            <DropdownMenuItem asChild className={rowClass}>
              <Link to="/tenant/$tenantId/users" params={{ tenantId: setupTenantId }}>
                <Settings className={iconClass} />
                <span className="truncate">{t('setup')}</span>
              </Link>
            </DropdownMenuItem>
          ) : null}

          <DropdownMenuSeparator className="mx-2.5 my-1.5" />

          {isThemeSwitcherEnabled ? (
            <DropdownMenuSub>
              <DropdownMenuSubTrigger
                className={cn(rowClass, '[&>svg:last-child]:text-muted-foreground')}
              >
                <ThemeIcon className={iconClass} />
                <span className="truncate">{t('shellTheme')}</span>
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="w-40 rounded-xl p-1.5">
                {themeOptions.map(([value, label, Icon]) => (
                  <DropdownMenuItem
                    key={value}
                    onSelect={() => setTheme(value)}
                    className={rowClass}
                  >
                    <Icon className={iconClass} />
                    <span>{label}</span>
                    {theme === value && <Check className="ml-auto h-4 w-4 text-primary" />}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          ) : null}

          <DropdownMenuSub>
            <DropdownMenuSubTrigger
              className={cn(rowClass, '[&>svg:last-child]:text-muted-foreground')}
            >
              <Globe className={iconClass} />
              <span className="truncate">{t('language')}</span>
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="w-40 rounded-xl p-1.5">
              {(['en', 'de'] as const).map((lang) => (
                <DropdownMenuItem
                  key={lang}
                  onSelect={() => lang !== currentLang && changeLanguage(lang)}
                  className={rowClass}
                >
                  <span>{lang === 'en' ? 'English' : 'Deutsch'}</span>
                  {lang === currentLang && <Check className="ml-auto h-4 w-4 text-primary" />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuSubContent>
          </DropdownMenuSub>

          <DropdownMenuSeparator className="mx-2.5 my-1.5" />

          <DropdownMenuItem onSelect={handleLogout} className={rowClass}>
            <LogOut className={iconClass} />
            <span>{t('shellLogout')}</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <TenantSwitcherDialog open={tenantDialogOpen} onOpenChange={setTenantDialogOpen} />
    </>
  );
};
